import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/common/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Sprout, ShieldAlert, ArrowRight, Lock, Mail } from "lucide-react";

const LoginPage = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleDemoEnter = (e) => {
    e && e.preventDefault && e.preventDefault();
    localStorage.setItem(
      "sarbajaya_demo_session",
      JSON.stringify({ user: "Sunita Debbarma", role: "Senior Bookkeeper", at: Date.now() }),
    );
    navigate("/app");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F9F8F3]">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-emerald-900/8 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-[#D95D39]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
        {/* Left panel */}
        <div className="hidden flex-col justify-between bg-[#0F4C3A] p-10 text-emerald-50 lg:flex">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D95D39] text-white shadow-lg">
              <Sprout className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold tracking-tight" data-testid="login-brand-name">{t.appName}</div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-200/70">
                {t.appTagline}
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              {t.appSlogan}
            </h2>
            <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-[#F8B195]" data-testid="login-brand-org">{t.appOrgFull}</p>
            <p className="mt-4 text-sm leading-relaxed text-emerald-100/80">
              A unified portal for Cluster Level Federations to manage Village Organizations,
              Self Help Groups, loans and repayments — in English and বাংলা.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { v: "14", l: "CLFs" },
                { v: "112", l: "VOs" },
                { v: "1,480", l: "SHGs" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl border border-emerald-50/10 bg-emerald-50/5 p-3">
                  <div className="font-mono-tabular text-xl font-bold">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-200/70">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-emerald-200/60">
            {t.appOrg} · {t.login.poweredBy}
          </div>
        </div>

        {/* Right panel - form */}
        <div className="flex flex-col px-6 py-8 sm:px-10 lg:px-14 lg:py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F4C3A] text-white">
                <Sprout className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold text-emerald-950">{t.appName}</div>
                <div className="text-[10px] text-emerald-900/60">{t.appTagline}</div>
              </div>
            </div>
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>

          <div className="mx-auto mt-10 w-full max-w-md lg:mt-20">
            <h1 className="font-display text-3xl font-bold tracking-tight text-emerald-950 sm:text-4xl">
              {t.login.welcome}
            </h1>
            <p className="mt-2 text-sm text-emerald-900/60">{t.login.subtitle}</p>

            <div
              className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
              data-testid="login-security-notice"
            >
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p>{t.login.disclaimer}</p>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleDemoEnter}>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-emerald-900/70">
                  {t.login.email}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-900/40" />
                  <Input
                    id="email"
                    type="text"
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="staff@clf.tripura.gov.in"
                    className="h-11 pl-9"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-emerald-900/70">
                  {t.login.password}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-900/40" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 pl-9"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 text-xs text-emerald-900/70">
                  <Checkbox data-testid="login-remember-checkbox" />
                  {t.login.remember}
                </label>
                <a href="#" className="text-xs font-semibold text-emerald-800 hover:text-emerald-900">
                  {t.login.forgot}
                </a>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="h-11 w-full bg-[#0F4C3A] text-white hover:bg-emerald-900"
              >
                {t.login.signIn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="relative my-2 flex items-center">
                <div className="h-px flex-1 bg-emerald-950/10" />
                <span className="px-3 text-[10px] uppercase tracking-widest text-emerald-900/40">or</span>
                <div className="h-px flex-1 bg-emerald-950/10" />
              </div>

              <Button
                type="button"
                variant="outline"
                data-testid="login-demo-btn"
                onClick={handleDemoEnter}
                className="h-11 w-full border-emerald-900/20 text-emerald-900 hover:bg-emerald-900/5"
              >
                {t.login.demoLogin}
              </Button>
            </form>

            <p className="mt-10 text-center text-[11px] text-emerald-900/50" data-testid="login-brand-footer">
              {t.appSlogan}<br />{t.appOrg} · {t.login.poweredBy}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
